import axios from 'axios';
import * as zxcvbn from 'ts-zxcvbn';
import * as uuid from 'uuid';

function _wrapRegExp() {
  _wrapRegExp = function (re, groups) {
    return new BabelRegExp(re, void 0, groups);
  };

  var _super = RegExp.prototype,
      _groups = new WeakMap();

  function BabelRegExp(re, flags, groups) {
    var _this = new RegExp(re, flags);

    return _groups.set(_this, groups || _groups.get(re)), _setPrototypeOf(_this, BabelRegExp.prototype);
  }

  function buildGroups(result, re) {
    var g = _groups.get(re);

    return Object.keys(g).reduce(function (groups, name) {
      return groups[name] = result[g[name]], groups;
    }, Object.create(null));
  }

  return _inherits(BabelRegExp, RegExp), BabelRegExp.prototype.exec = function (str) {
    var result = _super.exec.call(this, str);

    return result && (result.groups = buildGroups(result, this)), result;
  }, BabelRegExp.prototype[Symbol.replace] = function (str, substitution) {
    if ("string" == typeof substitution) {
      var groups = _groups.get(this);

      return _super[Symbol.replace].call(this, str, substitution.replace(/\$<([^>]+)>/g, function (_, name) {
        return "$" + groups[name];
      }));
    }

    if ("function" == typeof substitution) {
      var _this = this;

      return _super[Symbol.replace].call(this, str, function () {
        var args = arguments;
        return "object" != typeof args[args.length - 1] && (args = [].slice.call(args)).push(buildGroups(args, _this)), substitution.apply(this, args);
      });
    }

    return _super[Symbol.replace].call(this, str, substitution);
  }, _wrapRegExp.apply(this, arguments);
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  Object.defineProperty(subClass, "prototype", {
    writable: false
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

const NUMBER = {
  DEFAULT_CONVERSATION: 0,
  DEFAULT_DAY_COUNT: 0,
  DEFAULT_MAXIMUM_Y_AXIS: 5,
  HUNDRED: 100,
  INCREMENTATION_ONE: 1,
  MAXIMUM_RATIO: 1,
  MINIMUM_RATIO: 0,
  NUMBER_OF_PARAPHRASES: 21,
  ONE_DIGIT: 1,
  TEN: 10,
  THREE_DECIMALS: 3,
  TWO_DIGITS: 2,
  VALID_PASSWORD_SCORE: 3,
  ZERO: 0
};
const ARRAY = {
  EMPTY_LENGTH: 0,
  FIRST_INDEX: 0,
  ONE_ITEM_LENGTH: 1,
  SECOND_INDEX: 1,
  TWO_ITEM_LENGTH: 2
};
const STRING = {
  EMPTY_LENGTH: 0,
  FIRST_CHARACTER_INDEX: 0,
  FIRST_NEGATIVE_CHARACTER_INDEX: -1,
  SECOND_CHARACTER_INDEX: 1,
  THIRD_CHARACTER_INDEX: 2
};
const PASSWORD = {
  MINIMUM_STRENGTH: 3,
  SALT_ROUNDS: 10
};
const TIME = {
  DAYS_IN_A_WEEK: 7,
  FIFTEEN_SECONDS: 15,
  HOUR_IN_A_DAY: 24,
  // eslint-disable-next-line unicorn/numeric-separators-style
  MILLISECONDS_IN_A_DAY: 86400000,
  // eslint-disable-next-line unicorn/numeric-separators-style
  MILLISECONDS_IN_A_MINUTE: 60000,
  MILLISECONDS_IN_A_SECOND: 1000,
  MILLISECONDS_IN_A_SECOND_AND_A_HALF: 1500,
  MILLISECONDS_IN_THREE_SECOND: 3000,
  SECONDS_FOR_A_LOG: 3,
  SECONDS_IN_AN_HOUR: 3600,
  // eslint-disable-next-line unicorn/numeric-separators-style
  SECONDS_IN_A_DECADE: 315576000,
  SECONDS_IN_A_MINUTE: 60,
  // eslint-disable-next-line unicorn/numeric-separators-style
  SECONDS_IN_A_MONTH: 2592000,
  SECONDS_IN_FIFTEEN_MINUTES: 900,
  SECONDS_IN_HALF_AN_HOUR: 1800
};
const DATE = {
  DEFAULT_END_DATE: new Date('2970-01-01T00:00:00Z'),
  DEFAULT_START_DATE: new Date('1970-01-01T00:00:00Z')
};

const isDomainError = (maybeError, errorsList) => Object.values(errorsList).includes(maybeError);

const getEnvironment = processEnvironment => variableName => {
  const variable = processEnvironment[variableName];

  if (typeof variable === 'undefined') {
    throw new TypeError(`"${variableName}" environment does not exist`);
  }

  return variable;
};

const getEnvironments = processEnvironment => variableNames => {
  const missingEnvironmentVariables = variableNames.filter(variableName => {
    return typeof processEnvironment[variableName] === 'undefined';
  });

  if (missingEnvironmentVariables.length > ARRAY.EMPTY_LENGTH) {
    throw new TypeError(`Missing environment variables: "${missingEnvironmentVariables.join(',')}"`);
  }

  return variableNames.map(variableName => {
    return processEnvironment[variableName];
  });
};

const isObject = maybeObject => maybeObject instanceof Object;

const sanitizeError = maybeError => {
  if (typeof maybeError === 'string') {
    return new Error(maybeError);
  }

  if (maybeError instanceof Error) {
    return maybeError;
  }

  if (maybeError instanceof Object) {
    return new Error(JSON.stringify(maybeError));
  }

  return new Error('Unexpected error');
};

const HTTP_CODE = {
  BAD_REQUEST: 400,
  CONFLICT: 409,
  FORBIDDEN: 403,
  INTERNAL_ERROR: 500,
  METHOD_NOT_ALLOWED: 405,
  MOVED_PERMANENTLY: 301,
  NOT_ACCEPTABLE: 406,
  NOT_FOUND: 404,
  NO_CONTENT: 204,
  OK: 200,
  PAYMENT_REQUIRED: 402,
  REDIRECT: 302,
  REQUEST_ENTITY_TOO_LARGE: 413,
  UNAUTHORIZED: 401,
  UNSUPPORTED_MEDIA_TYPE: 415
};

const isString = data => typeof data === 'string';

const isNumber = data => typeof data === 'number';

const isJson = json => {
  try {
    JSON.parse(json);
  } catch (_a) {
    return false;
  }

  return true;
};

const isEmpty = data => {
  if (typeof data === 'string') {
    return data.trim().length === STRING.EMPTY_LENGTH;
  }

  if (Array.isArray(data)) {
    return data.length === ARRAY.EMPTY_LENGTH;
  }

  if (typeof data === 'object') {
    return Object.keys(data).length === STRING.EMPTY_LENGTH;
  }

  return true;
};

const isEmailValid = email => /^(?:(?![\t-\r @\xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF])[\s\S])+@(?:(?![\t-\r @\xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF])[\s\S])+\.(?:(?![\t-\r @\xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF])[\s\S])+$/.test(email);

const isProfessionalEmail = email => /^(?:[\0-\t\x0B\f\x0E-\u2027\u202A-\uD7FF\uE000-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF])+?@(?:(?!gmail\.com|hotmail\.fr|hotmail\.com|yahoo\.com|yahoo\.fr|jupimail\.com|spam\.la|yopmail\.com|yopmail\.com|spamfree24\.org|dontreg\.com|10minutemail\.com|jetable\.org|mail\x2Dtemporaire\.fr|get2mail\.fr|email\x2Djetable\.fr)(?:[\0-\t\x0B\f\x0E-\u2027\u202A-\uD7FF\uE000-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))+$/.test(email);

const isWebsiteUrlValid = website => // eslint-disable-next-line unicorn/no-unsafe-regex
/^(?!\.)(?:ht{2}ps?:\/{2})?(?:[\x2D\.0-9A-Z_a-z]{2,}){0,100}(?:(?:(?:\.|\/)(?:[0-9A-Z_a-z]|[&\x2D\.=\?\{\}]){2,}){1,100})(?:\/?)$/.test(website);

const cleanUrl = url => {
  const regexArray = /*#__PURE__*/_wrapRegExp(/((?:[\0-\t\x0B\f\x0E-\u2027\u202A-\uD7FF\uE000-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF])*?)(\?|#)/, {
    cleanUrl: 1,
    params: 2
  }).exec(url);

  return regexArray === null || typeof regexArray.groups === 'undefined' ? url : regexArray.groups.cleanUrl;
};

const getUrlDomain = url => {
  const regexArray = /*#__PURE__*/_wrapRegExp(/http(s)?:\/\/(www\.)?(([0-9A-Z_a-z]|[0-9]|\.|\x2D)*)/, {
    secure: 1,
    web: 2,
    urlDomain: 3,
    urlDomainElement: 4
  }).exec(url);

  return regexArray === null || typeof regexArray.groups === 'undefined' ? null : regexArray.groups.urlDomain;
};

const convertWebsiteToName = website => website.replace(/^https*:\/\/(?:www\.)*(?=(?:[\0-\t\x0B\f\x0E-\u2027\u202A-\uD7FF\uE000-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))/, '');

const isStrongPassword = password => {
  const result = zxcvbn.default(password);
  return result.score >= PASSWORD.MINIMUM_STRENGTH;
};

const generateUniqueId = () => uuid.v4();

const longStringLength = 128;

const generateRandomString = (requiredLength = longStringLength, allowedCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789') => {
  const selectOneRandomCharacter = () => {
    return allowedCharacters.charAt(Math.floor(Math.random() * allowedCharacters.length));
  };

  const buildString = (requiredLength, generatedString) => {
    if (generatedString.length < requiredLength) {
      const newGeneratedString = generatedString + selectOneRandomCharacter();
      return buildString(requiredLength, newGeneratedString);
    }

    return generatedString;
  };

  return buildString(requiredLength, '');
};

const generateRandomNumber = (max = 1000000) => Math.floor(Math.random() * max);

const generateRandomTagId = () => {
  return `${generateRandomString(20)}${generateRandomNumber()}`;
};

const capitalizeString = string => string.replace(/^[0-9A-Z_a-z]/, firstLetter => firstLetter.toUpperCase());

const generatedRandomPassword = (requiredLength = longStringLength, alphaMin = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', alphaMaj = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ', numbers = '0123456789', symbols = '!@#$%^&*_-+=') => {
  const randomChar = chars => {
    return chars.charAt(Math.floor(Math.random() * chars.length));
  };

  const buildPassword = (requiredLength, generatedString) => {
    if (generatedString.length < requiredLength) {
      const newGeneratedString = generatedString + randomChar(alphaMaj) + randomChar(alphaMin) + randomChar(numbers) + randomChar(symbols);
      return buildPassword(requiredLength, newGeneratedString);
    }

    return generatedString;
  };

  return buildPassword(requiredLength, '');
};

const defaultBasePathApi = 'https://api.sendinblue.com/v3';
const defaultHeadersApi = {
  Accept: 'application/json',
  'content-type': 'application/json'
};

const sendTransacEmail = apiKey => async bodySendSmtpEmail => {
  const url = `${defaultBasePathApi}/smtp/email`;

  try {
    await axios.post(url, bodySendSmtpEmail, {
      headers: Object.assign(Object.assign({}, defaultHeadersApi), {
        'api-key': apiKey
      })
    });
  } catch (error) {
    throw sanitizeError(error);
  }
};

const formatDate = (date, type = 'number-with-year') => {
  switch (type) {
    case 'number':
      {
        const month = `0${date.getMonth() + 1}`.slice(-2);
        const day = `0${date.getDate()}`.slice(-2);
        return `${month}-${day}`;
      }

    case 'number-with-year':
      {
        const year = `${date.getFullYear()}`;
        const month = `0${date.getMonth() + 1}`.slice(-2);
        const day = `0${date.getDate()}`.slice(-2);
        return `${year}-${month}-${day}`;
      }

    case 'text':
      {
        const localeDate = date.toLocaleString('en-GB', {
          day: 'numeric',
          month: 'short'
        });
        return `${localeDate.split(' ').join(' ')}.`;
      }

    case 'text-with-year':
      {
        const year = `${date.getFullYear()}`;
        const localeDate = date.toLocaleString('en-GB', {
          day: 'numeric',
          month: 'short'
        });
        return `${localeDate.split(' ').join(' ')}. ${year}`;
      }

    case 'dd/mm/yyyy':
      {
        const year = `${date.getFullYear()}`;
        const month = `0${date.getMonth() + 1}`.slice(-2);
        const day = `0${date.getDate()}`.slice(-2);
        return `${day}/${month}/${year}`;
      }

    default:
      return '';
  }
};

const getLocalDayOfWeekFromDate = date => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
};

const getUtcDayOfWeekFromDate = date => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getUTCDay()];
};

const getHoursDiff = (startDate, endDate) => {
  const msInHour = 1000 * 60 * 60;
  return Math.floor(Math.abs(endDate.getTime() - startDate.getTime()) / msInHour);
};

const getDateEveryHourInPeriod = (startDate, endDate) => {
  const hoursDiff = getHoursDiff(startDate, endDate);
  const indexesArray = [...Array.from({
    length: hoursDiff
  })].map((item, index) => index + 1);
  const dateMap = new Map();
  dateMap.set(0, new Date(startDate));
  const everyHourinPeriodReduce = indexesArray.reduce((accumulator, currentValue) => {
    const temporaryDateToAdd = new Date(startDate);
    temporaryDateToAdd.setHours(temporaryDateToAdd.getHours() + currentValue);
    accumulator.set(currentValue, temporaryDateToAdd);
    return accumulator;
  }, dateMap);
  const everyHourInPeriod = [...everyHourinPeriodReduce.values()];
  return everyHourInPeriod;
};

const getUniqueDaysAndHoursInPeriod = (startDate, endDate) => {
  const dateEveryHourInPeriod = getDateEveryHourInPeriod(startDate, endDate);
  const daysAndHoursInPeriod = dateEveryHourInPeriod.map(date => {
    const day = getUtcDayOfWeekFromDate(date);
    const hourFromDate = date.getUTCHours();
    const hour = hourFromDate < NUMBER.TEN ? `0${hourFromDate}` : hourFromDate.toString();
    return {
      day,
      hour
    };
  });
  return daysAndHoursInPeriod.filter((value, index, self) => index === self.findIndex(dayAndHour => dayAndHour.day === value.day && dayAndHour.hour === value.hour));
};

const getLastWeekDate = () => {
  const actualDate = new Date();
  actualDate.setUTCHours(0, 0, 0, 0);
  return new Date(actualDate.setUTCDate(actualDate.getUTCDate() - TIME.DAYS_IN_A_WEEK));
};

const getOnePeriod = periodName => {
  switch (periodName) {
    case 'yesterday':
      {
        const date = new Date();
        date.setDate(date.getDate() - NUMBER.ONE_DIGIT);
        return {
          endDate: new Date(date.setUTCHours(23, 59, 59, 999)),
          startDate: new Date(date.setUTCHours(0, 0, 0, 0))
        };
      }

    default:
      {
        const date = new Date();
        return {
          endDate: new Date(date.setUTCHours(23, 59, 59, 999)),
          startDate: new Date(date.setUTCHours(0, 0, 0, 0))
        };
      }
  }
};

const getStartOfDayDate = date => {
  const newDate = date;
  newDate.setUTCHours(0, 0, 0, 0);
  return newDate;
};

const getEndOfDayDate = date => {
  const newDate = date;
  newDate.setUTCHours(23, 59, 59, 999);
  return newDate;
};

const convertDateToString = date => {
  const year = `${date.getFullYear()}`;
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const day = `0${date.getDate()}`.slice(-2);
  return `${year}-${month}-${day}`;
};

const wait = async (millisecondsDelay = 1000) => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
    }, millisecondsDelay);
  });
};

const isValidPhoneNumber = phoneNumber => {
  // eslint-disable-next-line unicorn/no-unsafe-regex
  const regexPhone = /(((\+|00)33[\t-\r \xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]?|0)[1-9](([\t-\r \xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]?|\.)[0-9]{2}){4})/;
  return regexPhone.test(phoneNumber);
};

const getNbDaysBetweenTwoDates = (dateStart, dateEnd) => {
  dateStart.setUTCHours(0, 0, 0, 0);
  dateEnd.setUTCHours(0, 0, 0, 0);
  const differenceTimeTwoDates = dateEnd.getTime() + TIME.MILLISECONDS_IN_A_DAY - dateStart.getTime();
  return differenceTimeTwoDates / TIME.MILLISECONDS_IN_A_DAY;
};

export { ARRAY, DATE, HTTP_CODE, NUMBER, PASSWORD, STRING, TIME, capitalizeString, cleanUrl, convertDateToString, convertWebsiteToName, formatDate, generateRandomNumber, generateRandomString, generateRandomTagId, generateUniqueId, generatedRandomPassword, getDateEveryHourInPeriod, getEndOfDayDate, getEnvironment, getEnvironments, getHoursDiff, getLastWeekDate, getLocalDayOfWeekFromDate, getNbDaysBetweenTwoDates, getOnePeriod, getStartOfDayDate, getUniqueDaysAndHoursInPeriod, getUrlDomain, isDomainError, isEmailValid, isEmpty, isJson, isNumber, isObject, isProfessionalEmail, isString, isStrongPassword, isValidPhoneNumber, isWebsiteUrlValid, sanitizeError, sendTransacEmail, wait };
